import { h, Fragment } from "preact";
import { Line } from "@ant-design/charts";
import { DatePicker, Typography } from "antd";
import useProfile from "./useProfile";
import { useState, useEffect, useMemo } from "preact/hooks";
import moment from "moment";

const TeamActivityGraph = ({ roles }) => {
  const { teammates, options } = useProfile();

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const queryParams = {
    defaultDateFrom: moment().subtract(24, "hours"),
    defaultDateTo: moment(),
  };
  const initialState = {
    dateFrom: null,
    dateTo: null,
  };

  const [filters, setFilters] = useState(initialState);

  const disabledDateRange = (current) => current > queryParams.defaultDateTo;
  const onDateRangeChange = (dates) => {
    if (!dates) {
      setFilteredData(data);
      setFilters({ ...filters, dateFrom: null, dateTo: null });
      return;
    }

    const [selectedDateFrom, selectedDateTo] = dates;
    if (!selectedDateFrom || !selectedDateTo) return;

    const dateFrom = selectedDateFrom.startOf("day").unix();
    const dateTo = selectedDateTo.endOf("day").unix();

    const filtData = data.filter(
      (d) => d.timestamp >= dateFrom * 1000 && d.timestamp <= dateTo * 1000
    );
    setFilteredData(filtData);
    setFilters({ ...filters, dateFrom, dateTo });
  };

  const convertDateFormats = () => {
    return filters.dateFrom && filters.dateTo
      ? [moment.unix(filters.dateFrom), moment.unix(filters.dateTo)]
      : null;
  };

  function sortByCreatedTimestamp(teammates) {
    return teammates.sort((teammate1, teammate2) => {
      return teammate1.createdTimestamp - teammate2.createdTimestamp;
    });
  }

  const getGraphData = () => {
    const sortedTeammates = sortByCreatedTimestamp(teammates.slice());
    const newData = [];
    let prev = null;

    sortedTeammates.forEach((teammate) => {
      const date = new Date(teammate.createdTimestamp);
      const formattedDate = `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear().toString().slice(-2)}`;
      if (prev && prev.date === formattedDate) {
        prev.value++;
      } else {
        prev = {
          date: formattedDate,
          value: prev ? prev.value + 1 : 1, // Initial value
          timestamp: teammate.createdTimestamp,
        };
        newData.push(prev);
      }
    });
    setData(newData);
    setFilteredData(newData);
  };

  useEffect(() => {
    getGraphData();
  }, [teammates]);

  const config = useMemo(
    () => ({
      data: filteredData,
      xField: "date",
      yField: "value",
      xAxis: {
        title: {
          text: "Date",
        },
      },
      yAxis: {
        title: {
          text: "Members",
        },
        nice: true,
        minInterval: 1,
        tickCount: filteredData.length + 1,
        label: {
          formatter: (v) => `${Math.round(v)}`,
        },
      },
      point: {
        shape: "diamond | circule",
        style: (point) => {
          return {
            fill: "#0000FF",
            stroke: "#0000FF",
            lineWidth: 2,
          };
        },
      },
    }),
    [filteredData]
  );

  return (
    <div className="graph-roles">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Typography.Title level={4}>Team Activity</Typography.Title>
        <DatePicker.RangePicker
          allowClear
          value={convertDateFormats()}
          disabledDate={disabledDateRange}
          onCalendarChange={onDateRangeChange}
        />
      </div>
      <div
        style={{
          display: "flex",
          marginTop: "30px",
        }}
      >
        <div className="activity-graph">
          <Line className="" {...config} />
        </div>
        <div>
          <div className="roles-stats">
            <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
              {roles ? (
                Object.keys(roles).map((role) => (
                  <li
                    className="status open"
                    key={role}
                    style={{ textWrap: "nowrap" }}
                  >
                    <span>
                      {role}
                      {!filters.dateFrom && !filters.dateTo
                        ? ": " + roles[role]
                        : ""}
                    </span>
                  </li>
                ))
              ) : (
                <p> Loading roles.. </p>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
export default TeamActivityGraph;
